import React, { useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import ModalPopup from '../common/modalPopup';

const essentialCardStyles = {
  seance1: 'bg-[#D1E3E3]',
  missionA: 'bg-[#F1F6F6]',
  seance2: 'bg-[#D1E3E3]',
  parents: 'bg-white border-2 border-[#006367]'
};

const inDepthCardStyles = {
  seance1: 'bg-[#D1E3E3]',
  missionA: 'bg-[#F1F6F6]',
  seance2: 'bg-[#D1E3E3]',
  missionB: 'bg-[#F1F6F6]',
  seance3: 'bg-[#D1E3E3]',
  parents: 'bg-white border-2 border-[#006367]'
};

const textStyles = {
  seance1: 'text-[#006367]',
  missionA: 'text-[#006367]',
  seance2: 'text-[#006367]',
  seance3: 'text-[#006367]',
  missionB: 'text-[#006367]',
  parents: 'text-[#006367]'
};

const MobileCarousel = ({ cards, onCardClick, pathType }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? cards.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === cards.length - 1 ? 0 : prev + 1));
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrev();
  };

  return (
    <div className="relative px-2">
      <div className="relative flex items-center">
        {cards.length > 1 && (
          <button
            onClick={goToPrev}
            className="absolute -left-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center z-10">
            <IoIosArrowBack className="text-gray-500" size={16} />
          </button>
        )}

        <div
          className="w-full px-8 overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}>
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
            {cards.map((card, index) => (
              <div key={index} className="w-full flex-shrink-0 px-1">
                <ParcoursCard card={card} onClick={onCardClick} pathType={pathType} />
              </div>
            ))}
          </div>
        </div>

        {cards.length > 1 && (
          <button
            onClick={goToNext}
            className="absolute -right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center z-10">
            <IoIosArrowForward className="text-gray-500" size={16} />
          </button>
        )}
      </div>

      {cards.length > 1 && (
        <div className="flex justify-center gap-2 mt-5">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                index === currentIndex ? 'bg-[#006367] scale-110' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ParcoursCard = ({ card, onClick, pathType = 'essential', heightClass = '' }) => {
  const cardStylesMap = pathType === 'essential' ? essentialCardStyles : inDepthCardStyles;
  const bgStyle = cardStylesMap[card.type] || 'bg-gray-100';
  const txtStyle = textStyles[card.type] || 'text-[#006367]';

  return (
    <div
      onClick={() => onClick(card)}
      className={`${bgStyle} ${heightClass} rounded-3xl p-6 md:p-8 flex flex-col items-center text-center cursor-pointer transition-all duration-200 hover:shadow-md hover:brightness-[0.98] min-h-[220px] md:min-h-[260px]`}>
      <div className="relative mb-6 mt-2">
        <div
          className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
          dangerouslySetInnerHTML={{ __html: card.icon }}
        />
        {card.badge && (
          <span className="absolute right-[-12px] top-1/2 -translate-y-1/2 md:right-[-14px] w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#07484A] text-white flex items-center justify-center text-xs md:text-sm font-semibold">
            {card.badge}
          </span>
        )}
      </div>

      <h4
        className={`text-lg md:text-xl font-bold ${txtStyle} mb-2`}
        dangerouslySetInnerHTML={{ __html: card.title }}
      />

      {card.subtitle && (
        <p
          className={`text-sm md:text-base ${txtStyle} leading-relaxed font-medium`}
          dangerouslySetInnerHTML={{ __html: card.subtitle }}
        />
      )}
    </div>
  );
};

const PopupContent = ({ card, pathType = 'essential' }) => {
  const cardStylesMap = pathType === 'essential' ? essentialCardStyles : inDepthCardStyles;
  const txtStyle = textStyles[card.type] || 'text-[#006367]';

  const popupTitle = card.popupTitle || card.title || '';
  const popupDescription = card.popupDescription || card.subtitle || '';

  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative mb-6 mt-2">
        <div
          className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
          dangerouslySetInnerHTML={{ __html: card.icon }}
        />
        {card.badge && (
          <span className="absolute right-[-12px] top-1/2 -translate-y-1/2 md:right-[-14px] w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#07484A] text-white flex items-center justify-center text-xs md:text-sm font-semibold">
            {card.badge}
          </span>
        )}
      </div>

      {popupTitle && (
        <h4
          className={`text-lg md:text-xl font-bold ${txtStyle} mb-3`}
          dangerouslySetInnerHTML={{ __html: popupTitle }}
        />
      )}

      {popupDescription && (
        <p
          className={`text-sm md:text-base ${txtStyle} leading-relaxed font-medium`}
          dangerouslySetInnerHTML={{ __html: popupDescription }}
        />
      )}
    </div>
  );
};

const ParcoursEssentialBox = ({ translations }) => {
  return (
    <section className="py-4">
      <div className="bg-[#F1F6F6] rounded-2xl p-8 md:p-12 shadow-sm my-4">
        <div className="flex flex-col items-center text-center">
          <img src={translations?.logo} className="h-12 md:h-20 w-auto mb-8" alt="banner" />
          <p
            className="text-gray-700 text-sm md:text-base leading-relaxed"
            dangerouslySetInnerHTML={{ __html: translations?.description }}
          />
        </div>
      </div>
    </section>
  );
};

const EventsSection = ({ translations }) => {
  return (
    <section className="py-4">
      <div className="container mx-auto max-w-5xl px-4 md:px-6 bg-[#006367] p-14 rounded-2xl">
        <h2
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center"
          dangerouslySetInnerHTML={{ __html: translations?.title }}
        />
        <p
          className="text-white text-center text-sm md:text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: translations?.description }}
        />
      </div>
    </section>
  );
};

const ParcoursSection = ({
  title,
  subtitle,
  subtitle2,
  cards,
  boxWidth,
  onCardClick,
  pathType = 'essential',
  boxTranslations = null
}) => {
  return (
    <div className="mb-10">
      <h3
        className="text-xl md:text-2xl font-bold text-[#515151] text-center mb-1"
        dangerouslySetInnerHTML={{ __html: title }}
      />

      {subtitle2 && (
        <div
          className="text-lg md:text-xl font-semibold text-gray-800"
          dangerouslySetInnerHTML={{ __html: subtitle2 }}
        />
      )}

      {subtitle && (
        <p
          className="text-sm md:text-base text-gray-600 text-center mb-6"
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />
      )}

      <div className={`hidden md:flex flex-wrap justify-center ${!subtitle ? 'mt-4' : ''}`}>
        {cards.map((card, index) => (
          <div key={index} className="p-1" style={{ width: boxWidth }}>
            <ParcoursCard
              card={card}
              onClick={onCardClick}
              pathType={pathType}
              heightClass="h-full"
            />
          </div>
        ))}
      </div>

      <div className="block md:hidden">
        <MobileCarousel cards={cards} onCardClick={onCardClick} pathType={pathType} />
      </div>
    </div>
  );
};

function EducationalResponseMF2026({ translations, currentLanguage }) {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedPathType, setSelectedPathType] = useState('essential');

  const educationalResponse = translations?.educationalResponse;
  const paths = translations?.paths;

  const handleInDepthCardClick = (card) => {
    setSelectedCard(card);
    setSelectedPathType('inDepth');
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedCard(null);
    setSelectedPathType('essential');
  };

  return (
    <>
      <section className="py-12 bg-white">
        <div className="container mx-auto max-w-5xl px-4 md:px-6">
          <div className="flex items-start justify-center mb-10">
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#006367] text-center"
              dangerouslySetInnerHTML={{ __html: educationalResponse?.title }}
            />
          </div>

          {paths?.inDepthPath && (
            <ParcoursSection
              title={paths.inDepthPath.title}
              subtitle={paths.inDepthPath.subtitle}
              subtitle2={paths.inDepthPath.subtitle2}
              cards={paths.inDepthPath.cards || []}
              boxWidth="33.33%"
              onCardClick={handleInDepthCardClick}
              pathType="inDepth"
              boxTranslations={paths.parcoursInDepthBox}
            />
          )}

          {paths?.implicationPath && (
            <ParcoursSection
              title={paths.implicationPath.title}
              subtitle={paths.implicationPath.subtitle}
              subtitle2={paths.implicationPath.subtitle2}
              cards={paths.implicationPath.cards || []}
              boxWidth="50%"
              onCardClick={handleInDepthCardClick}
              pathType="inDepth"
            />
          )}

          {paths?.parcoursInDepthBox && ['de'].includes(currentLanguage) && (
            <ParcoursEssentialBox translations={paths.parcoursInDepthBox} />
          )}

          {paths?.events && <EventsSection translations={paths.events} />}
        </div>
      </section>

      <ModalPopup
        showPopup={showPopup}
        popupDisplayHandler={handleClosePopup}
        maxWidth="w-[90%] md:w-[500px]"
        closeBackgroundColorCode="#33363F"
        className="rounded-3xl p-6 md:p-8">
        <div className="relative">
          {selectedCard && <PopupContent card={selectedCard} pathType={selectedPathType} />}
        </div>
      </ModalPopup>
    </>
  );
}

export { EducationalResponseMF2026 };
